import {
  isNetworkError,
  isTimeoutError,
  isRateLimitError,
} from './error-type-guards';
import { RateLimitError } from './error-classes';

/**
 * Configuration for retry behavior.
 */
export interface IRetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds before first retry (default: 1000) */
  baseDelayMs?: number;
  /** Maximum delay cap in milliseconds (default: 30000) */
  maxDelayMs?: number;
  /** Jitter factor between 0 and 1, applied to delay to avoid thundering herd (default: 0.1) */
  jitterFactor?: number;
  /** Predicate that determines whether to retry a given error. Defaults to retrying network, timeout, and rate limit errors. */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  /** Callback invoked before each retry attempt */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
}

const DEFAULT_RETRY_CONFIG: Required<
  Pick<
    IRetryConfig,
    'maxRetries' | 'baseDelayMs' | 'maxDelayMs' | 'jitterFactor'
  >
> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 30_000,
  jitterFactor: 0.1,
};

/**
 * Calculates delay with exponential backoff and jitter.
 *
 * @param attempt - Zero-based attempt index
 * @param baseDelayMs - Base delay in milliseconds
 * @param maxDelayMs - Maximum delay cap in milliseconds
 * @param jitterFactor - Jitter factor between 0 and 1
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelayMs: number = DEFAULT_RETRY_CONFIG.baseDelayMs,
  maxDelayMs: number = DEFAULT_RETRY_CONFIG.maxDelayMs,
  jitterFactor: number = DEFAULT_RETRY_CONFIG.jitterFactor,
): number {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, maxDelayMs);
  const jitter = cappedDelay * jitterFactor * Math.random();
  return Math.floor(cappedDelay + jitter);
}

/**
 * Default predicate: retries on network errors, timeouts, rate limits, and 5xx server errors.
 */
function defaultShouldRetry(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  if (isTimeoutError(error)) return true;
  if (isRateLimitError(error)) return true;
  return false;
}

/**
 * Executes an async operation with automatic retry and exponential backoff.
 *
 * @param operation - The async function to execute
 * @param config - Retry configuration
 * @returns The result of the operation
 * @throws The last error encountered after all retries are exhausted
 *
 * @example
 * const data = await withRetry(
 *   () => fetchUserData(userId),
 *   {
 *     maxRetries: 3,
 *     onRetry: (err, attempt) => addBreadcrumb({ message: `Retry attempt ${attempt}` }),
 *   },
 * );
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  config: IRetryConfig = {},
): Promise<T> {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries,
    baseDelayMs = DEFAULT_RETRY_CONFIG.baseDelayMs,
    maxDelayMs = DEFAULT_RETRY_CONFIG.maxDelayMs,
    jitterFactor = DEFAULT_RETRY_CONFIG.jitterFactor,
    shouldRetry = defaultShouldRetry,
    onRetry,
  } = config;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt >= maxRetries || !shouldRetry(error, attempt)) {
        throw error;
      }

      let delayMs: number;

      if (error instanceof RateLimitError && error.retryAfterMs) {
        delayMs = error.retryAfterMs;
      } else {
        delayMs = calculateBackoffDelay(
          attempt,
          baseDelayMs,
          maxDelayMs,
          jitterFactor,
        );
      }

      onRetry?.(error, attempt + 1, delayMs);

      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}
